import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Box,
    Typography,
    Chip,
    Stack,
    Divider,
} from "@mui/material";
import { useUpdateOrderStatusMutation } from "../../../store/orders/ordersApis";

const OrderStatusDialog = ({ open, onClose, order, onSuccess }) => {
    const [handleUpdateStatus] = useUpdateOrderStatusMutation();
    const [selectedStatus, setSelectedStatus] = useState("");
    const [notes, setNotes] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (order) {
            setSelectedStatus(order.payment_status || "");
            setNotes("");
        }
    }, [order]);

    const handleSubmit = async () => {
        if (!order || !selectedStatus) return;

        setLoading(true);
        try {
            await handleUpdateStatus({
                order_id: order.id,
                payment_status: selectedStatus,
                notes: notes || `Status changed to ${selectedStatus} by admin`
            }).unwrap();

            onSuccess?.();
            onClose();
        } catch (error) {
            console.error("Error updating order status:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedStatus("");
        setNotes("");
        onClose();
    };

    const getStatusColor = (status) => {
        const statusColors = {
            pending: { bg: '#fff3cd', color: '#856404' },
            initiated: { bg: '#cce5ff', color: '#004085' },
            partial: { bg: '#ffeaa7', color: '#6c5ce7' },
            paid: { bg: '#d4edda', color: '#155724' },
            failed: { bg: '#f8d7da', color: '#721c24' },
            cancelled: { bg: '#f5c6cb', color: '#721c24' },
            refunded: { bg: '#e2e3e5', color: '#383d41' },
        };
        return statusColors[status] || { bg: '#f8f9fa', color: '#6c757d' };
    };

    const statusOptions = [
        { value: "pending", label: "Pending" },
        { value: "initiated", label: "Initiated" },
        { value: "partial", label: "Partial" },
        { value: "paid", label: "Paid" },
        { value: "failed", label: "Failed" },
        { value: "cancelled", label: "Cancelled" },
        { value: "refunded", label: "Refunded" },
    ];

    if (!order) return null;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="h6">Update Order Status</Typography>
                    <Chip
                        label={order.order_number}
                        size="small"
                        sx={{
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            fontWeight: 600,
                        }}
                    />
                </Stack>
            </DialogTitle>
            
            <DialogContent>
                <Stack spacing={3}>
                    {/* Order Summary */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Order Summary
                        </Typography>
                        <Stack spacing={1}>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" color="text.secondary">
                                    Customer:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {order.customer?.name || "N/A"}
                                </Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" color="text.secondary">
                                    Product:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {order.product?.name || "N/A"}
                                </Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" color="text.secondary">
                                    Total Amount:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    ₹{order.total_amount?.toLocaleString() || "0"}
                                </Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" color="text.secondary">
                                    Current Status:
                                </Typography>
                                <Chip
                                    label={order.payment_status?.toUpperCase() || "N/A"}
                                    size="small"
                                    sx={{
                                        backgroundColor: getStatusColor(order.payment_status).bg,
                                        color: getStatusColor(order.payment_status).color,
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                    }}
                                />
                            </Stack>
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Status Selection */}
                    <FormControl fullWidth>
                        <InputLabel>New Payment Status</InputLabel>
                        <Select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            label="New Payment Status"
                        >
                            {statusOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip
                                            label={option.label}
                                            size="small"
                                            sx={{
                                                backgroundColor: getStatusColor(option.value).bg,
                                                color: getStatusColor(option.value).color,
                                                fontWeight: 600,
                                                fontSize: '0.7rem',
                                            }}
                                        />
                                    </Stack>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Notes */}
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Notes (Optional)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add any notes about this status change..."
                        variant="outlined"
                    />

                    {/* Status Change Impact */}
                    {selectedStatus && selectedStatus !== order.payment_status && (
                        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                Status Change Impact
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedStatus === "paid" && "Order will be marked as fully paid. Remaining amount will be set to 0."}
                                {selectedStatus === "partial" && "Order will be marked as partially paid. Payment tracking will continue."}
                                {selectedStatus === "cancelled" && "Order will be cancelled. All payments will be reset."}
                                {selectedStatus === "refunded" && "Order will be marked as refunded. All payments will be reset."}
                                {selectedStatus === "failed" && "Order payment will be marked as failed. Customer can retry payment."}
                                {selectedStatus === "pending" && "Order will be reset to pending status."}
                                {selectedStatus === "initiated" && "Order payment will be marked as initiated."}
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !selectedStatus || selectedStatus === order.payment_status}
                >
                    {loading ? "Updating..." : "Update Status"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrderStatusDialog;
